import React, { useState, useEffect } from 'react';
import { GitBranch, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { itineraryVersionService } from '../../services/itineraryVersionService';

interface Hotel {
  hotel_name: string;
  checkin_date: string | null;
  checkout_date: string | null;
}

interface VersionInfo {
  itinerary_id: number;
  version_number: number;
  itinerary_status: string;
  booked_items_count: number;
  hotels: Hotel[];
}

interface QuotationVersionPanelProps {
  itineraryId: number;
}

const fmtDate = (d: string | null) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch { return d; }
};

function getHotelChanges(prev: Hotel[], curr: Hotel[]): string[] {
  const changes: string[] = [];
  const prevNames = prev.map(h => h.hotel_name.toLowerCase().trim());
  const currNames = curr.map(h => h.hotel_name.toLowerCase().trim());

  prev.forEach(h => {
    if (!currNames.includes(h.hotel_name.toLowerCase().trim()))
      changes.push(`Removed: ${h.hotel_name}`);
  });
  curr.forEach(h => {
    if (!prevNames.includes(h.hotel_name.toLowerCase().trim()))
      changes.push(`Added: ${h.hotel_name}`);
  });
  curr.forEach(h => {
    const match = prev.find(p => p.hotel_name.toLowerCase().trim() === h.hotel_name.toLowerCase().trim());
    if (match && (match.checkin_date !== h.checkin_date || match.checkout_date !== h.checkout_date)) {
      changes.push(`${h.hotel_name}: dates changed (${fmtDate(match.checkin_date)}–${fmtDate(match.checkout_date)} → ${fmtDate(h.checkin_date)}–${fmtDate(h.checkout_date)})`);
    }
  });
  return changes;
}

const STATUS_BADGE: Record<string, string> = {
  CONVERTED: 'bg-green-100 text-green-800',
  ARCHIVED: 'bg-gray-100 text-gray-600',
  PENDING_CONFIRMATION: 'bg-yellow-100 text-yellow-800',
};

const QuotationVersionPanel: React.FC<QuotationVersionPanelProps> = ({ itineraryId }) => {
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [quotationNumber, setQuotationNumber] = useState('');
  const [hasQuotation, setHasQuotation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { fetchData(); }, [itineraryId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await itineraryVersionService.getQuotationForItinerary(itineraryId);
      const data = res.data;
      if (data.has_quotation === false) { setHasQuotation(false); return; }
      setHasQuotation(true);
      setQuotationNumber(data.quotation_number);
      setVersions(data.versions || []);
    } catch (err) {
      console.error('Error fetching quotation data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
    </div>
  );

  if (!hasQuotation) return null;

  // Check if any version has hotel changes — show warning in header
  const anyHotelChanges = versions.some((v, idx) => {
    if (idx === 0) return false;
    return getHotelChanges(versions[idx - 1].hotels || [], v.hotels || []).length > 0;
  });

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-900">
            {quotationNumber} — {versions.length} version{versions.length !== 1 ? 's' : ''}
          </span>
          {anyHotelChanges && (
            <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              <AlertTriangle className="w-3 h-3" /> Hotel changes
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>

      {expanded && (
        <div className="border-t border-gray-200 p-3">
          <div className="text-xs font-medium text-gray-500 uppercase mb-2">Version History</div>
          <div className="space-y-2">
            {versions.map((v, idx) => {
              const prev = idx > 0 ? versions[idx - 1] : null;
              const hotelChanges = prev ? getHotelChanges(prev.hotels || [], v.hotels || []) : [];
              const isCurrent = v.itinerary_id === itineraryId;

              return (
                <div
                  key={v.itinerary_id}
                  className={`rounded-lg border p-2.5 text-sm ${isCurrent ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
                >
                  {/* Row header */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-gray-500">v{v.version_number}</span>
                    <span className="text-xs text-gray-400 font-normal">WH_{v.itinerary_id}</span>
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${STATUS_BADGE[v.itinerary_status] || 'bg-gray-100 text-gray-600'}`}>
                      {v.itinerary_status}
                    </span>
                    {isCurrent && <span className="text-xs text-blue-600">(current)</span>}
                    {v.booked_items_count > 0 && (
                      <span className="text-xs text-gray-400 ml-auto">{v.booked_items_count} booked</span>
                    )}
                  </div>

                  {/* Hotels */}
                  {v.hotels && v.hotels.length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      {v.hotels.map((h, hi) => (
                        <div key={hi} className="flex items-center gap-1.5 text-xs text-gray-600">
                          <span>🏨</span>
                          <span className="font-medium">{h.hotel_name}</span>
                          {h.checkin_date && (
                            <span className="text-gray-400">{fmtDate(h.checkin_date)} – {fmtDate(h.checkout_date)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Hotel change flags */}
                  {hotelChanges.length > 0 && (
                    <div className="mt-1.5 p-1.5 bg-amber-50 border border-amber-200 rounded">
                      <div className="flex items-center gap-1 text-xs font-medium text-amber-700 mb-1">
                        <AlertTriangle className="w-3 h-3" /> Hotel changes from v{prev!.version_number}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-1">
                        <div>
                          <div className="text-[10px] text-gray-500 font-medium mb-0.5">v{prev!.version_number} (previous)</div>
                          {(prev!.hotels || []).map((h, hi) => (
                            <div key={hi} className="text-xs text-red-700">🏨 {h.hotel_name} {h.checkin_date ? `· ${fmtDate(h.checkin_date)}–${fmtDate(h.checkout_date)}` : ''}</div>
                          ))}
                          {(prev!.hotels || []).length === 0 && <div className="text-xs text-gray-400 italic">No hotels</div>}
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 font-medium mb-0.5">v{v.version_number} (current)</div>
                          {(v.hotels || []).map((h, hi) => (
                            <div key={hi} className="text-xs text-green-700">🏨 {h.hotel_name} {h.checkin_date ? `· ${fmtDate(h.checkin_date)}–${fmtDate(h.checkout_date)}` : ''}</div>
                          ))}
                          {(v.hotels || []).length === 0 && <div className="text-xs text-gray-400 italic">No hotels</div>}
                        </div>
                      </div>
                      {hotelChanges.map((c, ci) => (
                        <div key={ci} className="text-xs text-amber-700">• {c}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationVersionPanel;
